import { clsx } from "clsx";
import moment from "moment-timezone";
import { twMerge } from "tailwind-merge";
function cn(...inputs) {
  return twMerge(clsx(inputs));
}
function debounce(func, wait, immediate = false) {
  let timeoutId;
  return function(...args) {
    const later = () => {
      timeoutId = null;
      if (!immediate) {
        func.apply(this, args);
      }
    };
    const callNow = immediate && !timeoutId;
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    timeoutId = setTimeout(later, wait);
    if (callNow) {
      func.apply(this, args);
    }
  };
}
const kebabToPascalCase = (str) => {
  const processed = str.replace(/[-_]([a-z0-9])/gi, (_, char) => char.toUpperCase());
  return processed.charAt(0).toUpperCase() + processed.slice(1);
};
const formatDisplayDate = (dateString, timezone) => {
  if (dateString instanceof Date) {
    dateString = dateString.toISOString();
  }
  if (!dateString || dateString.length === 0 || typeof dateString !== "string") {
    return "";
  }
  const hasTime = dateString.includes(":");
  const isISOFormat = dateString.includes("T") || dateString.includes("Z");
  let day, month, year, isToday, isCurrentYear;
  if (timezone && isISOFormat) {
    const dateMoment = moment.tz(dateString, timezone);
    const todayMoment = moment.tz(timezone);
    day = dateMoment.date();
    month = dateMoment.month();
    year = dateMoment.year();
    isToday = dateMoment.isSame(todayMoment, "day");
    isCurrentYear = year === todayMoment.year();
  } else {
    const date = new Date(dateString);
    const today = /* @__PURE__ */ new Date();
    if (hasTime && !isISOFormat) {
      day = date.getDate();
      month = date.getMonth();
      year = date.getFullYear();
      isToday = date.toDateString() === today.toDateString();
      isCurrentYear = year === today.getFullYear();
    } else {
      day = date.getUTCDate();
      month = date.getUTCMonth();
      year = date.getUTCFullYear();
      isToday = date.toISOString().slice(0, 10) === today.toISOString().slice(0, 10);
      isCurrentYear = year === today.getUTCFullYear();
    }
  }
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const monthName = months[month];
  if (isToday) {
    return `${day} ${monthName}`;
  }
  return isCurrentYear ? `${day} ${monthName}` : `${day} ${monthName} ${year}`;
};
const formatDisplayTime = (dateString, timezone) => moment(dateString).tz(timezone).format("h:mma");
const formatTimestamp = (timestamp) => {
  const date = new Date(timestamp);
  const now = /* @__PURE__ */ new Date();
  if (isNaN(date.getTime())) {
    return "Unknown";
  }
  const diffMs = now.getTime() - date.getTime();
  if (diffMs < 0) {
    return "Just now";
  }
  const diffMins = Math.floor(diffMs / (1e3 * 60));
  const diffHours = Math.floor(diffMs / (1e3 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1e3 * 60 * 60 * 24));
  if (diffMins < 1) {
    return "Just now";
  } else if (diffMins < 60) {
    return `${diffMins} min ago`;
  } else if (diffHours < 24) {
    return `${diffHours} hr ago`;
  } else if (diffDays === 1) {
    return "Yesterday";
  } else if (diffDays < 7) {
    return `${diffDays} days ago`;
  } else {
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: diffDays > 365 ? "numeric" : void 0
    });
  }
};
const formatNumber = (value) => {
  if (isNaN(value) || !isFinite(value)) {
    return "0";
  }
  return new Intl.NumberFormat("en-US").format(Math.round(value));
};
function abbreviateNumber(number) {
  const num = Number(number);
  if (num < 1e3) {
    return formatNumber(num);
  }
  if (num < 1e6) {
    const roundTo2 = num < 1e5 ? 100 : 1e3;
    const rounded2 = Math.round(num / roundTo2) * roundTo2;
    const abbreviated2 = rounded2 / 1e3;
    if (abbreviated2 === 1e3) {
      return "1M";
    }
    const formatted2 = abbreviated2 % 1 === 0 ? abbreviated2.toString() : abbreviated2.toFixed(1);
    return `${formatted2}k`;
  }
  const roundTo = 1e5;
  const rounded = Math.round(num / roundTo) * roundTo;
  const abbreviated = rounded / 1e6;
  const formatted = abbreviated % 1 === 0 ? abbreviated.toString() : abbreviated.toFixed(1);
  return `${formatted}M`;
}
const formatDuration = (seconds) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor(seconds % 3600 / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  if (hours <= 0) {
    if (minutes <= 0) {
      return `${remainingSeconds}s`;
    }
    return `${minutes}m ${remainingSeconds}s`;
  }
  return `${hours}h ${minutes}m ${remainingSeconds}s`;
};
const formatPercentage = (value) => {
  const percentage = value * 100;
  if (percentage === 0) {
    return "0%";
  } else if (percentage < 0.1) {
    return `${percentage.toFixed(2)}%`;
  } else if (percentage < 1) {
    return `${percentage.toFixed(1)}%`;
  }
  const rounded = Math.round(percentage);
  return `${new Intl.NumberFormat("en-US").format(rounded)}%`;
};
function getCountryFlag(countryCode) {
  if (!countryCode || countryCode === null || countryCode.toUpperCase() === "NULL" || countryCode === "ᴺᵁᴸᴸ" || countryCode === "ᴺᵁ") {
    return "🏳️";
  }
  return countryCode.toUpperCase().replace(
    /./g,
    (char) => String.fromCodePoint(char.charCodeAt(0) + 127397)
  );
}
const stringToHslColor = (str, saturation, lightness) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const h = hash % 360;
  return "hsl(" + h + ", " + saturation + "%, " + lightness + "%)";
};
export {
  abbreviateNumber,
  cn,
  debounce,
  formatDisplayDate,
  formatDisplayTime,
  formatDuration,
  formatNumber,
  formatPercentage,
  formatTimestamp,
  getCountryFlag,
  kebabToPascalCase,
  stringToHslColor
};
//# sourceMappingURL=ds-utils.js.map
